import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Dimensions, Pressable, StyleSheet, View} from 'react-native'
import {Image} from 'expo-image'
import {msg} from '@lingui/macro'
import {useLingui} from '@lingui/react'
import {useRequest} from 'ahooks'

import {emailRegExp, phoneNumberRegExp} from '#/lib/tools'
import {logger} from '#/logger'
import * as Toast from '#/view/com/util/Toast'
import QrScanner, {
  type QrScannerRefProps,
} from '#/screens/Profile/Header/QrScanner'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import * as TextField from '#/components/forms/TextField'
import {QrCode_Scan} from '#/components/icons/QrCode'
import * as Prompt from '#/components/Prompt'
import {Text} from '#/components/Typography'
import server from '#/server'
import {RiceError} from '#/server/rice'

const SCREEN_HEIGHT = Dimensions.get('window').height

export function SendPointsDialog({
  defaultReceive,
  defaultToUserId,
  defaultToUserDomainName,
  control,
  onUpdate,
}: {
  defaultReceive?: string
  defaultToUserId?: string
  defaultToUserDomainName?: string
  control: Dialog.DialogControlProps
  onUpdate?: () => void
}) {
  const {_} = useLingui()

  const searchParams = new URLSearchParams(window.location.search)
  const [receiveUser, setReceiveUser] = useState(
    searchParams.get('receiveUser') || '',
  )

  useEffect(() => {
    if ((receiveUser || defaultToUserId) && control) {
      control.open()
      const url = new URL(window.location.href)
      url.search = ''
      window.history.replaceState(null, '', url)
    }
  }, [receiveUser, defaultToUserId, control])

  const onPressCancel = useCallback(() => {
    control.close()
    setReceiveUser('')
  }, [control])

  return (
    <Dialog.Outer
      control={control}
      nativeOptions={{
        minHeight: SCREEN_HEIGHT,
      }}
      testID="sendPointsModal">
      <DialogInner
        onUpdate={onUpdate}
        defaultReceive={receiveUser}
        defaultToUserId={defaultToUserId}
        defaultToUserDomainName={defaultToUserDomainName}
        onPressCancel={onPressCancel}
      />
    </Dialog.Outer>
  )
}

function DialogInner({
  defaultReceive = '',
  defaultToUserId,
  defaultToUserDomainName,
  onUpdate,
  onPressCancel,
}: {
  defaultReceive?: string
  defaultToUserId?: string
  defaultToUserDomainName?: string
  onUpdate?: () => void
  onPressCancel: () => void
}) {
  const {_} = useLingui()
  const t = useTheme()
  const control = Dialog.useDialogContext()

  const videoRef = useRef<QrScannerRefProps>(null)

  const [giftAccount, setGiftAccount] = useState(defaultReceive)
  const [giftPoints, setGiftPoints] = useState('')
  const [giftRemark, setGiftRemark] = useState('')
  const [toUserId] = useState(defaultToUserId)
  const [toUserDomainName] = useState(defaultToUserDomainName)

  const recipientDisplay = giftAccount || toUserDomainName || toUserId

  const confirmPromptControl = Prompt.usePromptControl()
  const resultPromptControl = Prompt.usePromptControl()
  const duplicatePromptControl = Prompt.usePromptControl()
  const [resultMessage, setResultMessage] = useState('')
  const [isSuccess, setIsSuccess] = useState(false)

  // 余额取不到时**不能当成 0** —— 那样任何金额都会被判为无效,而无效只有
  // 「请输入正确格式」一句文案,于是登录失效被报成了输入格式错误(2026-08-22
  // 的线上问题)。这里把「还在加载」「加载失败」「余额不足」三种情况分开。
  const {
    data: userDetail,
    loading: balanceLoading,
    error: balanceError,
  } = useRequest(() => server.rice.getCurrentUser())

  const balance = userDetail?.grain_balance
  const balanceUnavailable = !balanceLoading && balance === undefined

  const handleSend = useCallback(async () => {
    try {
      // rice 的 `to` 一个字段就够:id / DID / handle / 邮箱 / 手机号都认,
      // 不用像 core 那样分 toUserId 和 userPhoneOrEmail 两个字段。
      await server.rice.createGrainTransfer({
        to: toUserId || giftAccount,
        amount: Number(giftPoints),
        kind: 'gift',
        memo: giftRemark || undefined,
      })
      onUpdate?.()
      setIsSuccess(true)
      setResultMessage(`成功向 ${recipientDisplay} 发送了 ${giftPoints} 稻米！`)

      try {
        const historyStr = localStorage.getItem('transfer_history')
        const history = historyStr ? JSON.parse(historyStr) : []
        history.push({
          recipient: toUserId || giftAccount,
          recipientDisplay,
          amount: Number(giftPoints),
          timestamp: Date.now(),
        })
        localStorage.setItem('transfer_history', JSON.stringify(history))
      } catch (e) {
        console.error('Failed to save transfer history', e)
      }
      resultPromptControl.open()
    } catch (e: any) {
      setIsSuccess(false)
      setResultMessage(String(e))
      resultPromptControl.open()
      logger.error('Failed to send points', {message: String(e)})
    }
  }, [
    onUpdate,
    resultPromptControl,
    recipientDisplay,
    giftAccount,
    giftPoints,
    giftRemark,
    toUserId,
  ])

  const onPressSave = useCallback(() => {
    try {
      const recipient = toUserId || giftAccount
      const amount = Number(giftPoints)
      const historyStr = localStorage.getItem('transfer_history')
      if (historyStr) {
        const history = JSON.parse(historyStr)
        const now = Date.now()
        const isDuplicate = history.some(
          (tx: any) =>
            tx.recipient === recipient &&
            tx.amount === amount &&
            now - tx.timestamp < 300000, // 5 mins in ms
        )
        if (isDuplicate) {
          duplicatePromptControl.open()
          return
        }
      }
    } catch (e) {
      console.error('Failed to parse transfer history', e)
    }
    confirmPromptControl.open()
  }, [
    confirmPromptControl,
    duplicatePromptControl,
    toUserId,
    giftAccount,
    giftPoints,
  ])

  const onConfirmDuplicate = useCallback(() => {
    handleSend()
  }, [handleSend])

  const handleResultClose = useCallback(() => {
    if (isSuccess) {
      control.close()
    }
  }, [isSuccess, control])

  const displayNameInvalid = useMemo(() => {
    if (toUserId) return false
    if (!giftAccount) return false

    const regs = [emailRegExp, phoneNumberRegExp]
    for (const reg of regs) {
      if (reg.test(giftAccount)) return false
    }

    return true
  }, [giftAccount, toUserId])

  /** `false` = 没问题,否则是要展示的那句话。 */
  const giftPointsError = useMemo(() => {
    if (!giftPoints) return false
    if (!/^[1-9]\d*$/.test(giftPoints)) return '请输入正整数'
    if (balance === undefined) return false
    if (Number(giftPoints) > balance) return `超出当前稻米余额（${balance}）`
    return false
  }, [giftPoints, balance])

  const giftPointsInvalid = Boolean(giftPointsError)

  const cancelButton = useCallback(
    () => (
      <Pressable
        accessibilityRole={'button'}
        style={[{marginRight: 15}]}
        onPress={onPressCancel}>
        <Image
          accessibilityIgnoresInvertColors
          source={require('#/assets/close.svg')}
          alt="close"
          style={[{width: 16, height: 16}]}
        />
      </Pressable>
    ),
    [onPressCancel],
  )

  const scan = async () => {
    const qrResult = await videoRef?.current?.scan().catch(error => {
      Toast.show(String(error), 'xmark')
    })
    if (!qrResult) return
    const url = new URL(qrResult)
    setGiftAccount(url.searchParams.get('receiveUser') || '')
  }

  return (
    <>
      <QrScanner ref={videoRef} />
      <Dialog.ScrollableInner
        label={_(msg`Edit profile`)}
        style={[a.overflow_hidden, {marginTop: 'calc(50vh - 40px - 170px)'}]}
        header={
          <Dialog.Header
            renderRight={cancelButton}
            style={[a.border_transparent]}>
            <Dialog.HeaderText>发送稻米</Dialog.HeaderText>
          </Dialog.Header>
        }>
        <View style={[a.gap_xl]}>
          {toUserId ? (
            <View>
              <Text style={[t.atoms.text_contrast_high]}>
                将向该用户发送稻米
              </Text>
            </View>
          ) : (
            <View style={styles.account}>
              <TextField.LabelText>手机号/邮箱</TextField.LabelText>
              <TextField.Root isInvalid={displayNameInvalid}>
                <Dialog.Input
                  defaultValue={giftAccount}
                  onChangeText={setGiftAccount}
                  label={_(msg`Display name`)}
                  placeholder={'请输入手机号/邮箱'}
                  testID="editProfileDisplayNameInput"
                />
              </TextField.Root>
              <Pressable
                accessibilityRole={'button'}
                style={styles.scan}
                onPress={scan}>
                <QrCode_Scan fill={'#6F869F'} size={'md'} style={[a.z_10]} />
              </Pressable>
              {displayNameInvalid && (
                <TextField.SuffixText
                  style={[
                    a.text_sm,
                    a.mt_xs,
                    a.font_bold,
                    {color: t.palette.negative_400},
                  ]}
                  label={'格式'}>
                  请输入正确格式
                </TextField.SuffixText>
              )}
            </View>
          )}

          <View>
            <TextField.LabelText>稻米</TextField.LabelText>
            <TextField.Root isInvalid={giftPointsInvalid}>
              <Dialog.Input
                defaultValue={giftPoints}
                onChangeText={setGiftPoints}
                label={_(msg`Display name`)}
                placeholder={'请输入赠送稻米'}
                testID="editProfileDescriptionInput"
                inputMode={'numeric'}
              />
            </TextField.Root>
            {giftPointsError && (
              <TextField.SuffixText
                style={[
                  a.text_sm,
                  a.mt_xs,
                  a.font_bold,
                  {color: t.palette.negative_400},
                ]}
                label={'格式'}>
                {giftPointsError}
              </TextField.SuffixText>
            )}
          </View>

          <View>
            <TextField.LabelText>附言</TextField.LabelText>
            <TextField.Root>
              <Dialog.Input
                defaultValue={giftRemark}
                onChangeText={setGiftRemark}
                label={_(msg`Remark`)}
                placeholder={'请输入附言（最多20字）'}
                testID="editProfileRemarkInput"
                maxLength={20}
              />
            </TextField.Root>
          </View>
          <View style={[a.flex_row]}>
            <Text style={styles.cur_pints_label}>当前稻米：</Text>
            <Text style={styles.cur_pints_value}>
              {balanceLoading ? '加载中…' : balance ?? '—'}
            </Text>
          </View>
          {balanceUnavailable && (
            <Text
              style={[a.text_sm, a.font_bold, {color: t.palette.negative_400}]}>
              {balanceError instanceof RiceError && balanceError.status === 401
                ? '登录已失效，请退出后重新登录再试。'
                : '暂时读不到你的稻米余额，请稍后重试。'}
            </Text>
          )}
          <Button
            label={_(msg`Save`)}
            onPress={onPressSave}
            disabled={
              (!toUserId && !giftAccount) ||
              !giftPoints ||
              displayNameInvalid ||
              giftPointsInvalid ||
              // 余额读不到就别让人发 —— 发出去多半也是 401,不如在这里说清楚
              balanceLoading ||
              balanceUnavailable
            }
            size="small"
            color="primary"
            variant="solid"
            style={[styles.confirm, a.rounded_sm]}
            testID="editProfileSaveBtn">
            <ButtonText style={[a.text_md]}>确认</ButtonText>
          </Button>
        </View>
      </Dialog.ScrollableInner>

      <Prompt.Basic
        control={duplicatePromptControl}
        title="重复操作提醒"
        description={`系统检测到您在5分钟内向该用户（${recipientDisplay}）发送过同等金额的稻米。您确定要再次发送吗？`}
        onConfirm={onConfirmDuplicate}
        confirmButtonCta="继续发送"
        cancelButtonCta="取消"
        confirmButtonColor="primary"
      />

      <Prompt.Basic
        control={confirmPromptControl}
        title="确认发送"
        description={`是否确认向 ${recipientDisplay} 发送 ${giftPoints} 稻米？`}
        onConfirm={handleSend}
        confirmButtonCta="确认"
        cancelButtonCta="取消"
      />

      <Prompt.Basic
        control={resultPromptControl}
        title={isSuccess ? '发送成功' : '发送失败'}
        description={resultMessage}
        onConfirm={handleResultClose}
        confirmButtonCta="确认"
        showCancel={false}
      />
    </>
  )
}

const styles = StyleSheet.create({
  account: {
    position: 'relative',
  },
  scan: {
    position: 'absolute',
    right: 0,
    top: 23,
    zIndex: 10,
    padding: 10,
  },
  cur_pints_label: {
    color: '#6F869F',
  },
  cur_pints_value: {
    color: '#42576C',
    fontWeight: 500,
  },
  confirm: {
    width: 76,
    margin: 'auto',
  },
})
